import React, { useEffect, useMemo, useState } from "react";
import { api, formatMXN, CATEGORIAS, fileToCompressedDataUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Plus, Search, ImagePlus, X, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_MO = [
  { nombre: "1 CARPINTERO", unidad: "JOR", cantidad: 0, precio_unitario: 1400 },
  { nombre: "AYUDANTE CARPINTERO", unidad: "JOR", cantidad: 0, precio_unitario: 500 },
  { nombre: "1 BARNIZADOR + 1 AYUDANTE", unidad: "JOR", cantidad: 0, precio_unitario: 1400 },
  { nombre: "AYUDANTE BARNIZADOR", unidad: "JOR", cantidad: 0, precio_unitario: 500 },
  { nombre: "INSTALADOR", unidad: "JOR", cantidad: 0, precio_unitario: 1400 },
  { nombre: "RESANADOR", unidad: "JOR", cantidad: 0, precio_unitario: 500 },
  { nombre: "SUPERVISIÓN", unidad: "%", cantidad: 0.05, precio_unitario: 0 },
  { nombre: "DESGASTE DE HERRAMIENTA", unidad: "%", cantidad: 0.03, precio_unitario: 0 },
];

const DEFAULT_FLETES = [
  { nombre: "FLETE DENTRO DE PLAYA", cantidad: 0, precio_unitario: 400 },
  { nombre: "FLETE CANCÚN", cantidad: 0, precio_unitario: 1500 },
  { nombre: "FLETE TULUM", cantidad: 0, precio_unitario: 2000 },
  { nombre: "FLETE PTO. MORELOS", cantidad: 0, precio_unitario: 1500 },
  { nombre: "FLETE PTO. AVENTURAS", cantidad: 0, precio_unitario: 1500 },
];

const emptyForm = {
  codigo: "",
  descripcion: "",
  unidad: "LOTE",
  precio_unitario: 0,
  categoria: "Residencial",
  cliente_id: "",
  cliente_nombre: "",
  proyecto: "",
  imagen_url: "",
  imagenes: [],
  materiales: [],
  mano_de_obra: [],
  fletes: [],
  indirecto_pct: 10,
  utilidad_pct: 15,
  comision_venta: 0,
};

const emptyMaterialLine = () => ({ _uid: `m_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, nombre: "", categoria_material: "TRIPLAY", unidad: "HOJA", cantidad: 1, precio_unitario: 0 });

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [materialesMaster, setMaterialesMaster] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filter, setFilter] = useState("Todas");
  const [filterCliente, setFilterCliente] = useState("__all__");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expandedCats, setExpandedCats] = useState({});
  const [expandedProyectos, setExpandedProyectos] = useState({});

  const load = async () => {
    const [c, m, cl] = await Promise.all([api.get("/catalog"), api.get("/materiales"), api.get("/clientes")]);
    setItems(c.data);
    setMaterialesMaster(m.data);
    setClientes(cl.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...emptyForm,
      ...it,
      imagenes: it.imagenes || (it.imagen_url ? [it.imagen_url] : []),
      materiales: it.materiales || [],
      mano_de_obra: it.mano_de_obra || [],
      fletes: it.fletes || [],
      indirecto_pct: it.indirecto_pct ?? 10,
      utilidad_pct: it.utilidad_pct ?? 15,
      comision_venta: it.comision_venta ?? 0,
    });
    setOpen(true);
  };

  // --- Totales calculados ---
  const subtotalMateriales = useMemo(
    () => (form.materiales || []).reduce((s, m) => s + Number(m.cantidad || 0) * Number(m.precio_unitario || 0), 0),
    [form.materiales]
  );

  // Para mano de obra: subtotal de filas no-%
  const subtotalMOBase = useMemo(
    () => (form.mano_de_obra || [])
      .filter((m) => m.unidad !== "%")
      .reduce((s, m) => s + Number(m.cantidad || 0) * Number(m.precio_unitario || 0), 0),
    [form.mano_de_obra]
  );

  // Para filas con unidad="%", el importe = cantidad * subtotalMOBase
  const calcMORowImporte = (m) => {
    if (m.unidad === "%") return Number(m.cantidad || 0) * subtotalMOBase;
    return Number(m.cantidad || 0) * Number(m.precio_unitario || 0);
  };
  const subtotalMO = useMemo(
    () => (form.mano_de_obra || []).reduce((s, m) => {
      if (m.unidad === "%") return s + Number(m.cantidad || 0) * subtotalMOBase;
      return s + Number(m.cantidad || 0) * Number(m.precio_unitario || 0);
    }, 0),
    [form.mano_de_obra, subtotalMOBase]
  );

  const subtotalFlete = useMemo(
    () => (form.fletes || []).reduce((s, f) => s + Number(f.cantidad || 0) * Number(f.precio_unitario || 0), 0),
    [form.fletes]
  );

  const subtotalCosto = subtotalMateriales + subtotalMO + subtotalFlete;
  const indirecto = subtotalCosto * (Number(form.indirecto_pct || 0) / 100);
  const utilidad = subtotalCosto * (Number(form.utilidad_pct || 0) / 100);
  const comision = Number(form.comision_venta || 0);
  const subtotalSinIVA = subtotalCosto + indirecto + utilidad + comision;

  // Auto-sync: el precio unitario de la partida = subtotal sin IVA
  // SOLO si hay un desglose con valor > 0. Si no, respeta el precio existente
  // y deja el campo editable manualmente.
  useEffect(() => {
    if (subtotalSinIVA <= 0) return;
    setForm((f) => {
      const val = Number(subtotalSinIVA.toFixed(2));
      if (Number(f.precio_unitario) === val) return f;
      return { ...f, precio_unitario: val };
    });
  }, [subtotalSinIVA]);

  const save = async () => {
    if (!form.codigo || !form.descripcion) { toast.error("Código y descripción son obligatorios"); return; }
    try {
      const payload = {
        ...form,
        precio_unitario: Number(form.precio_unitario) || 0,
        imagen_url: form.imagenes?.[0] || form.imagen_url || null,
        imagenes: form.imagenes || [],
        materiales: (form.materiales || []).map((m) => ({ ...m, cantidad: Number(m.cantidad) || 0, precio_unitario: Number(m.precio_unitario) || 0 })),
        mano_de_obra: (form.mano_de_obra || []).map((m) => ({ ...m, cantidad: Number(m.cantidad) || 0, precio_unitario: Number(m.precio_unitario) || 0 })),
        fletes: (form.fletes || []).map((f) => ({ ...f, cantidad: Number(f.cantidad) || 0, precio_unitario: Number(f.precio_unitario) || 0 })),
        indirecto_pct: Number(form.indirecto_pct) || 0,
        utilidad_pct: Number(form.utilidad_pct) || 0,
        comision_venta: Number(form.comision_venta) || 0,
      };
      if (editing) await api.put(`/catalog/${editing.id}`, payload);
      else await api.post("/catalog", payload);
      toast.success(editing ? "Partida actualizada" : "Partida creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta partida?")) return;
    await api.delete(`/catalog/${id}`);
    toast.success("Eliminada");
    load();
  };

  const duplicar = async (id, codigo) => {
    try {
      const { data } = await api.post(`/catalog/${id}/duplicar`);
      toast.success(`${codigo} duplicada como ${data.codigo}`);
      await load();
      // abrir la nueva partida en edición
      openEdit(data);
    } catch {
      toast.error("Error al duplicar");
    }
  };

  // --- Imágenes ---
  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const current = form.imagenes || [];
    const slots = Math.max(0, 10 - current.length);
    if (slots === 0) { toast.error("Máximo 10 imágenes"); return; }
    const sel = files.slice(0, slots);
    const compressed = await Promise.all(sel.map((f) => fileToCompressedDataUrl(f)));
    setForm({ ...form, imagenes: [...current, ...compressed] });
    e.target.value = "";
  };
  const removeImage = (idx) => setForm({ ...form, imagenes: (form.imagenes || []).filter((_, i) => i !== idx) });

  // --- Materiales sub-table ---
  const setMaterial = (idx, patch) => {
    const next = [...(form.materiales || [])];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, materiales: next });
  };
  const removeMaterial = (idx) => setForm({ ...form, materiales: (form.materiales || []).filter((_, i) => i !== idx) });
  const addMaterial = (preset) => {
    const line = preset
      ? { nombre: preset.nombre, categoria_material: preset.categoria_material, unidad: preset.unidad, cantidad: 1, precio_unitario: preset.precio_unitario }
      : emptyMaterialLine();
    setForm({ ...form, materiales: [...(form.materiales || []), line] });
  };
  const pickMasterMaterial = (idx, nombre) => {
    const master = materialesMaster.find((x) => x.nombre === nombre);
    if (master) setMaterial(idx, { nombre: master.nombre, categoria_material: master.categoria_material, unidad: master.unidad, precio_unitario: master.precio_unitario });
    else setMaterial(idx, { nombre });
  };

  // --- Mano de obra ---
  const setMO = (idx, patch) => {
    const next = [...(form.mano_de_obra || [])];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, mano_de_obra: next });
  };
  const removeMO = (idx) => setForm({ ...form, mano_de_obra: (form.mano_de_obra || []).filter((_, i) => i !== idx) });
  const addMORow = () => setForm({ ...form, mano_de_obra: [...(form.mano_de_obra || []), { nombre: "", unidad: "JOR", cantidad: 0, precio_unitario: 0 }] });
  const loadDefaultMO = () => setForm({ ...form, mano_de_obra: DEFAULT_MO.map((x) => ({ ...x })) });

  // --- Flete ---
  const setFlete = (idx, patch) => {
    const next = [...(form.fletes || [])];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, fletes: next });
  };
  const removeFlete = (idx) => setForm({ ...form, fletes: (form.fletes || []).filter((_, i) => i !== idx) });
  const addFleteRow = () => setForm({ ...form, fletes: [...(form.fletes || []), { nombre: "", cantidad: 0, precio_unitario: 0 }] });
  const loadDefaultFletes = () => setForm({ ...form, fletes: DEFAULT_FLETES.map((x) => ({ ...x })) });

  const applyAsPrecio = () => setForm({ ...form, precio_unitario: Number(subtotalSinIVA.toFixed(2)) });

  const materialesByCat = useMemo(() => {
    const groups = {};
    for (const m of materialesMaster) {
      if (!m || !m.id || !m.nombre) continue;
      const k = m.categoria_material || "OTROS";
      if (!groups[k]) groups[k] = [];
      groups[k].push(m);
    }
    return groups;
  }, [materialesMaster]);

  // Union of categories from master + already on form
  const allCategoriesOrdered = useMemo(() => {
    const set = new Set();
    for (const m of materialesMaster) set.add(m.categoria_material || "OTROS");
    for (const m of (form.materiales || [])) set.add(m.categoria_material || "OTROS");
    const order = ["MATERIA PRIMA", "HERRAJES", "INSUMOS", "CUBIERTAS", "BARNIZ", "OTROS"];
    return Array.from(set).sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [materialesMaster, form.materiales]);

  const rowsOfCategory = (cat) =>
    (form.materiales || [])
      .map((m, idx) => ({ m, idx }))
      .filter(({ m }) => (m.categoria_material || "OTROS") === cat);

  const subtotalOfCategory = (cat) =>
    rowsOfCategory(cat).reduce((s, { m }) => s + Number(m.cantidad || 0) * Number(m.precio_unitario || 0), 0);

  const mkUid = (prefix = "m") => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  const addEmptyLineForCategory = (cat) => {
    setForm({
      ...form,
      materiales: [...(form.materiales || []), { _uid: mkUid(), nombre: "", categoria_material: cat, unidad: "PZA", cantidad: 1, precio_unitario: 0 }],
    });
    setExpandedCats((s) => ({ ...s, [cat]: true }));
  };

  const addMasterToCategory = (cat, masterId) => {
    const m = materialesMaster.find((x) => x.id === masterId);
    if (!m) return;
    setForm({
      ...form,
      materiales: [...(form.materiales || []), { _uid: mkUid(), nombre: m.nombre, categoria_material: cat, unidad: m.unidad, cantidad: 1, precio_unitario: m.precio_unitario }],
    });
    setExpandedCats((s) => ({ ...s, [cat]: true }));
  };

  const filtered = items.filter((i) => {
    const matchCat = filter === "Todas" || i.categoria === filter;
    const matchCli = filterCliente === "__all__" || i.cliente_id === filterCliente;
    const q = search.toLowerCase();
    const matchSearch = !q
      || (i.codigo || "").toLowerCase().includes(q)
      || (i.descripcion || "").toLowerCase().includes(q)
      || (i.proyecto || "").toLowerCase().includes(q)
      || (i.cliente_nombre || "").toLowerCase().includes(q);
    return matchCat && matchCli && matchSearch;
  });

  return (
    <div className="space-y-8" data-testid="catalog-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase-label">Base de datos</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Catálogo de partidas</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Define códigos reutilizables con precio construido desde materiales, mano de obra y flete.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="catalog-new-item">
          <Plus className="w-4 h-4 mr-2" /> Nueva partida
        </Button>
      </header>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="catalog-search" placeholder="Buscar por código, descripción, proyecto o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
        </div>
        <Select value={filterCliente} onValueChange={setFilterCliente}>
          <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="catalog-filter-cliente"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent className="max-h-[320px]">
            <SelectItem value="__all__">Todos los clientes</SelectItem>
            {clientes.filter((c) => c?.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="catalog-filter-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Todas">Todas las categorías</SelectItem>
            {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {(() => {
        // Group filtered items by categoría (proyecto)
        const groups = {};
        for (const it of filtered) {
          const cat = it.categoria || "Sin categoría";
          if (!groups[cat]) groups[cat] = [];
          groups[cat].push(it);
        }
        const order = ["Residencial", "Comercial", "Hoteleria"];
        const catKeys = Object.keys(groups).sort((a, b) => {
          const ia = order.indexOf(a), ib = order.indexOf(b);
          if (ia === -1 && ib === -1) return a.localeCompare(b);
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
        if (filtered.length === 0) {
          return <div className="border border-border bg-white rounded-lg py-10 text-center text-muted-foreground">Sin partidas.</div>;
        }
        return (
          <div className="space-y-3">
            {catKeys.map((cat) => {
              const rows = groups[cat];
              const isOpen = expandedProyectos[cat] ?? true;
              const groupSubtotal = rows.reduce((s, r) => s + Number(r.precio_unitario || 0), 0);
              return (
                <div key={cat} className="border border-border bg-white rounded-lg overflow-hidden" data-testid={`proj-group-${cat}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedProyectos((s) => ({ ...s, [cat]: !isOpen }))}
                    className="w-full px-4 py-3 bg-[#2f3d31]/10 hover:bg-[#2f3d31]/15 flex items-center justify-between transition-colors"
                    data-testid={`proj-toggle-${cat}`}
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-[#2f3d31]" /> : <ChevronRight className="w-4 h-4 text-[#2f3d31]" />}
                      <span className="font-display text-lg text-[#2f3d31]">{cat}</span>
                      <span className="text-xs text-muted-foreground">· {rows.length} {rows.length === 1 ? "partida" : "partidas"}</span>
                    </div>
                    <span className="text-sm tabular text-muted-foreground">Suma P.U.: {formatMXN(groupSubtotal)}</span>
                  </button>

                  {isOpen && (
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F2EC]/70 text-left">
                        <tr>
                          <th className="px-4 py-2 uppercase-label w-20"></th>
                          <th className="px-4 py-2 uppercase-label">Código</th>
                          <th className="px-4 py-2 uppercase-label">Descripción</th>
                          <th className="px-4 py-2 uppercase-label">Cliente / Proyecto</th>
                          <th className="px-4 py-2 uppercase-label">Unidad</th>
                          <th className="px-4 py-2 uppercase-label text-right">P.U.</th>
                          <th className="px-4 py-2 uppercase-label w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((it) => (
                          <tr key={it.id} className="border-t border-border align-top" data-testid={`catalog-row-${it.codigo}`}>
                            <td className="px-4 py-3">{(it.imagenes?.[0] || it.imagen_url) ? <img src={it.imagenes?.[0] || it.imagen_url} alt={it.codigo} className="w-14 h-14 rounded object-cover border border-border" /> : <div className="w-14 h-14 rounded bg-[#F4F2EC] border border-border" />}</td>
                            <td className="px-4 py-3 font-medium tabular">{it.codigo}</td>
                            <td className="px-4 py-3 text-foreground/80 max-w-md"><div className="line-clamp-3">{it.descripcion}</div></td>
                            <td className="px-4 py-3 text-xs">
                              {it.cliente_nombre && <div className="text-foreground">{it.cliente_nombre}</div>}
                              {it.proyecto && <div className="text-muted-foreground">{it.proyecto}</div>}
                              {!it.cliente_nombre && !it.proyecto && <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{it.unidad}</td>
                            <td className="px-4 py-3 text-right tabular">{formatMXN(it.precio_unitario)}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 justify-end">
                                <Button size="icon" variant="ghost" onClick={() => duplicar(it.id, it.codigo)} title="Duplicar partida" data-testid={`catalog-duplicate-${it.codigo}`}><Copy className="w-4 h-4 text-[#8B5A33]" /></Button>
                                <Button size="icon" variant="ghost" onClick={() => openEdit(it)} title="Editar" data-testid={`catalog-edit-${it.codigo}`}><Pencil className="w-4 h-4" /></Button>
                                <Button size="icon" variant="ghost" onClick={() => remove(it.id)} title="Eliminar" data-testid={`catalog-delete-${it.codigo}`}><Trash2 className="w-4 h-4 text-[#BC4749]" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Edit / create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl bg-white max-h-[92vh] overflow-y-auto"
          data-testid="catalog-form-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar partida" : "Nueva partida"}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="space-y-2"><Label>Código</Label><Input data-testid="form-codigo" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="CO-01" /></div>
            <div className="space-y-2"><Label>Unidad</Label><Input data-testid="form-unidad" value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} placeholder="LOTE" /></div>
            <div className="col-span-2 space-y-2"><Label>Descripción</Label><Textarea data-testid="form-descripcion" rows={4} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Precio unitario (MXN) <span className="text-[10px] text-muted-foreground">— se auto-calcula si hay desglose</span></Label>
              <Input data-testid="form-precio" type="number" step="0.01" value={form.precio_unitario} onChange={(e) => setForm({ ...form, precio_unitario: e.target.value })} className="tabular" />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="form-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cliente <span className="text-[10px] text-muted-foreground">— opcional, para búsquedas rápidas</span></Label>
              <Select
                value={form.cliente_id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") { setForm({ ...form, cliente_id: "", cliente_nombre: "" }); return; }
                  const c = clientes.find((x) => x.id === v);
                  setForm({ ...form, cliente_id: v, cliente_nombre: c?.nombre || "" });
                }}
              >
                <SelectTrigger data-testid="form-cliente"><SelectValue placeholder="Sin cliente" /></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  <SelectItem value="__none__">— Sin cliente —</SelectItem>
                  {clientes.filter((c) => c?.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Proyecto <span className="text-[10px] text-muted-foreground">— opcional</span></Label>
              <Input data-testid="form-proyecto" value={form.proyecto || ""} onChange={(e) => setForm({ ...form, proyecto: e.target.value })} placeholder="Casa Tulum, Hotel Ancla…" />
            </div>
          </div>

          {/* Imágenes */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <Label>Imágenes de referencia ({(form.imagenes || []).length}/10)</Label>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} data-testid="catalog-add-images" />
                <span className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-[#8B5A33] text-[#8B5A33] hover:bg-[#8B5A33]/10 rounded-md"><ImagePlus className="w-4 h-4" /> Agregar</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {(form.imagenes || []).map((src, idx) => (
                <div key={idx} className="relative"><img src={src} className="w-20 h-20 object-cover rounded border border-border" alt={`img-${idx}`} /><button onClick={() => removeImage(idx)} className="absolute -top-1.5 -right-1.5 bg-[#BC4749] text-white rounded-full p-0.5"><X className="w-3 h-3" /></button></div>
              ))}
              {(form.imagenes || []).length === 0 && (<div className="text-xs text-muted-foreground">Sin imágenes aún.</div>)}
            </div>
          </div>

          {/* MATERIALES */}
          <div className="mt-6 border-t border-border pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display text-xl">Materiales</div>
                <div className="text-xs text-muted-foreground">Despliega cada categoría para agregar renglones específicos.</div>
              </div>
              <div className="text-right">
                <div className="uppercase-label">Subtotal materiales</div>
                <div className="font-display text-lg tabular" data-testid="mat-subtotal">{formatMXN(subtotalMateriales)}</div>
              </div>
            </div>

            <div className="space-y-2">
              {allCategoriesOrdered.map((cat) => {
                const rows = rowsOfCategory(cat);
                const isOpen = expandedCats[cat] ?? rows.length > 0;
                const catSubtotal = subtotalOfCategory(cat);
                const mastersOfCat = materialesByCat[cat] || [];
                return (
                  <div key={cat} className="border border-border rounded overflow-hidden" data-testid={`mat-section-${cat}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedCats((s) => ({ ...s, [cat]: !isOpen }))}
                      className="w-full px-3 py-2.5 bg-[#2f3d31]/10 hover:bg-[#2f3d31]/15 flex items-center justify-between transition-colors"
                      data-testid={`mat-toggle-${cat}`}
                    >
                      <div className="flex items-center gap-2">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-[#2f3d31]" /> : <ChevronRight className="w-4 h-4 text-[#2f3d31]" />}
                        <span className="font-bold uppercase tracking-wider text-[12px] text-[#2f3d31]">{cat}</span>
                        <span className="text-xs text-muted-foreground">· {rows.length} {rows.length === 1 ? "renglón" : "renglones"}</span>
                      </div>
                      <span className="text-sm tabular font-semibold text-[#2f3d31]">{formatMXN(catSubtotal)}</span>
                    </button>

                    {isOpen && (
                      <div>
                        <table className="w-full text-sm">
                          <thead className="bg-[#F4F2EC]/70 text-left">
                            <tr>
                              <th className="px-2 py-2 uppercase-label">Material</th>
                              <th className="px-2 py-2 uppercase-label w-20">Unid.</th>
                              <th className="px-2 py-2 uppercase-label w-20">Cant.</th>
                              <th className="px-2 py-2 uppercase-label w-28 text-right">P.U.</th>
                              <th className="px-2 py-2 uppercase-label w-28 text-right">Importe</th>
                              <th className="w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length === 0 && (
                              <tr><td colSpan={6} className="px-3 py-3 text-center text-xs text-muted-foreground">Sin renglones en esta categoría. Agrega abajo ↓</td></tr>
                            )}
                            {rows.map(({ m, idx }) => {
                              const rowKey = m._uid || `mat-${idx}`;
                              const importe = Number(m.cantidad || 0) * Number(m.precio_unitario || 0);
                              const isCustom = m.nombre && !mastersOfCat.find((x) => x.nombre === m.nombre);
                              return (
                                <tr key={rowKey} className="border-t border-border" data-testid={`mat-row-${idx}`}>
                                  <td className="px-2 py-1.5">
                                    <Select value={m.nombre || "__none__"} onValueChange={(v) => pickMasterMaterial(idx, v === "__none__" ? "" : v)}>
                                      <SelectTrigger className="h-8" data-testid={`mat-select-${idx}`}><SelectValue placeholder="Selecciona material" /></SelectTrigger>
                                      <SelectContent className="max-h-[300px]">
                                        <SelectItem value="__none__">— sin material —</SelectItem>
                                        {isCustom && <SelectItem value={m.nombre}>{m.nombre} (personalizado)</SelectItem>}
                                        {mastersOfCat.map((x) => (
                                          <SelectItem key={x.id} value={x.nombre}>{x.nombre}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="px-2 py-1.5"><Input value={m.unidad} onChange={(e) => setMaterial(idx, { unidad: e.target.value })} className="h-8" /></td>
                                  <td className="px-2 py-1.5"><Input type="number" step="0.01" value={m.cantidad} onChange={(e) => setMaterial(idx, { cantidad: e.target.value })} className="h-8 text-right tabular" data-testid={`mat-cant-${idx}`} /></td>
                                  <td className="px-2 py-1.5"><Input type="number" step="0.01" value={m.precio_unitario} onChange={(e) => setMaterial(idx, { precio_unitario: e.target.value })} className="h-8 text-right tabular" data-testid={`mat-pu-${idx}`} /></td>
                                  <td className="px-2 py-1.5 text-right tabular text-sm">{formatMXN(importe)}</td>
                                  <td className="px-2 py-1.5"><Button size="icon" variant="ghost" onClick={() => removeMaterial(idx)}><Trash2 className="w-3.5 h-3.5 text-[#BC4749]" /></Button></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="px-3 py-2 border-t border-border flex flex-wrap items-center justify-end gap-2">
                          <Select onValueChange={(id) => addMasterToCategory(cat, id)}>
                            <SelectTrigger className="w-72 h-8" data-testid={`mat-add-master-${cat}`}>
                              <SelectValue placeholder={`+ Agregar del inventario (${mastersOfCat.length})`} />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {mastersOfCat.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Sin materiales registrados en {cat}.</div>}
                              {mastersOfCat.map((x) => (
                                <SelectItem key={x.id} value={x.id}>{x.nombre} · {formatMXN(x.precio_unitario)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" onClick={() => addEmptyLineForCategory(cat)} data-testid={`mat-add-empty-${cat}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Línea libre
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* MANO DE OBRA */}
          <div className="mt-6 border-t border-border pt-5">
            <div className="flex items-center justify-between mb-3">
              <div><div className="font-display text-xl">Mano de obra</div><div className="text-xs text-muted-foreground">Unidad "%" calcula sobre el subtotal de mano de obra base ({formatMXN(subtotalMOBase)}).</div></div>
              <div className="flex gap-2">
                {(form.mano_de_obra || []).length === 0 && <Button variant="outline" size="sm" onClick={loadDefaultMO} data-testid="mo-load-default">Cargar plantilla</Button>}
                <Button variant="outline" size="sm" onClick={addMORow} data-testid="mo-add"><Plus className="w-3.5 h-3.5 mr-1" /> Renglón</Button>
              </div>
            </div>
            <div className="border border-border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F2EC]/70 text-left">
                  <tr>
                    <th className="px-2 py-2 uppercase-label">Concepto</th>
                    <th className="px-2 py-2 uppercase-label w-20">Unid.</th>
                    <th className="px-2 py-2 uppercase-label w-24">Cant. / %</th>
                    <th className="px-2 py-2 uppercase-label w-28 text-right">P.U.</th>
                    <th className="px-2 py-2 uppercase-label w-28 text-right">Importe</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.mano_de_obra || []).length === 0 && (<tr><td colSpan={6} className="px-3 py-4 text-center text-xs text-muted-foreground">Sin mano de obra. Carga plantilla o agrega renglón.</td></tr>)}
                  {(form.mano_de_obra || []).map((m, idx) => {
                    const importe = calcMORowImporte(m);
                    return (
                      <tr key={idx} className="border-t border-border" data-testid={`mo-row-${idx}`}>
                        <td className="px-2 py-1.5"><Input value={m.nombre} onChange={(e) => setMO(idx, { nombre: e.target.value })} className="h-8" /></td>
                        <td className="px-2 py-1.5">
                          <Select value={m.unidad} onValueChange={(v) => setMO(idx, { unidad: v })}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="JOR">JOR</SelectItem>
                              <SelectItem value="%">%</SelectItem>
                              <SelectItem value="PZA">PZA</SelectItem>
                              <SelectItem value="HRS">HRS</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5"><Input type="number" step="0.0001" value={m.cantidad} onChange={(e) => setMO(idx, { cantidad: e.target.value })} className="h-8 text-right tabular" data-testid={`mo-cant-${idx}`} /></td>
                        <td className="px-2 py-1.5"><Input type="number" step="0.01" value={m.precio_unitario} onChange={(e) => setMO(idx, { precio_unitario: e.target.value })} disabled={m.unidad === "%"} className="h-8 text-right tabular" data-testid={`mo-pu-${idx}`} /></td>
                        <td className="px-2 py-1.5 text-right tabular text-sm">{formatMXN(importe)}</td>
                        <td className="px-2 py-1.5"><Button size="icon" variant="ghost" onClick={() => removeMO(idx)}><Trash2 className="w-3.5 h-3.5 text-[#BC4749]" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr><td colSpan={4} className="px-2 py-2 text-right uppercase-label bg-[#FFF7B2]">Subtotal mano de obra</td><td className="px-2 py-2 text-right tabular bg-[#FFF7B2] font-semibold" data-testid="mo-subtotal">{formatMXN(subtotalMO)}</td><td className="bg-[#FFF7B2]"></td></tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* FLETE */}
          <div className="mt-6 border-t border-border pt-5">
            <div className="flex items-center justify-between mb-3">
              <div><div className="font-display text-xl">Flete</div><div className="text-xs text-muted-foreground">Configura las zonas y deja cantidad 0 en las no aplicables.</div></div>
              <div className="flex gap-2">
                {(form.fletes || []).length === 0 && <Button variant="outline" size="sm" onClick={loadDefaultFletes} data-testid="flete-load-default">Cargar plantilla</Button>}
                <Button variant="outline" size="sm" onClick={addFleteRow} data-testid="flete-add"><Plus className="w-3.5 h-3.5 mr-1" /> Renglón</Button>
              </div>
            </div>
            <div className="border border-border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F2EC]/70 text-left">
                  <tr>
                    <th className="px-2 py-2 uppercase-label">Zona</th>
                    <th className="px-2 py-2 uppercase-label w-20">Cant.</th>
                    <th className="px-2 py-2 uppercase-label w-28 text-right">P.U.</th>
                    <th className="px-2 py-2 uppercase-label w-28 text-right">Importe</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.fletes || []).length === 0 && (<tr><td colSpan={5} className="px-3 py-4 text-center text-xs text-muted-foreground">Sin fletes.</td></tr>)}
                  {(form.fletes || []).map((f, idx) => {
                    const importe = Number(f.cantidad || 0) * Number(f.precio_unitario || 0);
                    return (
                      <tr key={idx} className="border-t border-border" data-testid={`flete-row-${idx}`}>
                        <td className="px-2 py-1.5"><Input value={f.nombre} onChange={(e) => setFlete(idx, { nombre: e.target.value })} className="h-8" /></td>
                        <td className="px-2 py-1.5"><Input type="number" step="0.01" value={f.cantidad} onChange={(e) => setFlete(idx, { cantidad: e.target.value })} className="h-8 text-right tabular" data-testid={`flete-cant-${idx}`} /></td>
                        <td className="px-2 py-1.5"><Input type="number" step="0.01" value={f.precio_unitario} onChange={(e) => setFlete(idx, { precio_unitario: e.target.value })} className="h-8 text-right tabular" data-testid={`flete-pu-${idx}`} /></td>
                        <td className="px-2 py-1.5 text-right tabular text-sm">{formatMXN(importe)}</td>
                        <td className="px-2 py-1.5"><Button size="icon" variant="ghost" onClick={() => removeFlete(idx)}><Trash2 className="w-3.5 h-3.5 text-[#BC4749]" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr><td colSpan={3} className="px-2 py-2 text-right uppercase-label bg-[#FFF7B2]">Subtotal flete</td><td className="px-2 py-2 text-right tabular bg-[#FFF7B2] font-semibold" data-testid="flete-subtotal">{formatMXN(subtotalFlete)}</td><td className="bg-[#FFF7B2]"></td></tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* TOTALES FINALES */}
          <div className="mt-6 border-t border-border pt-5">
            <div className="font-display text-xl mb-3">Totales</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label className="flex-1">Indirecto (%)</Label>
                  <Input type="number" step="0.01" value={form.indirecto_pct} onChange={(e) => setForm({ ...form, indirecto_pct: e.target.value })} className="w-24 text-right tabular" data-testid="tot-indirecto-pct" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label className="flex-1">Utilidad (%)</Label>
                  <Input type="number" step="0.01" value={form.utilidad_pct} onChange={(e) => setForm({ ...form, utilidad_pct: e.target.value })} className="w-24 text-right tabular" data-testid="tot-utilidad-pct" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label className="flex-1">Comisión por venta (MXN)</Label>
                  <Input type="number" step="0.01" value={form.comision_venta} onChange={(e) => setForm({ ...form, comision_venta: e.target.value })} className="w-32 text-right tabular" data-testid="tot-comision" />
                </div>
              </div>
              <div className="border border-border rounded p-3 text-sm">
                <div className="flex justify-between py-1"><span>Subtotal materiales</span><span className="tabular">{formatMXN(subtotalMateriales)}</span></div>
                <div className="flex justify-between py-1"><span>Subtotal mano de obra</span><span className="tabular">{formatMXN(subtotalMO)}</span></div>
                <div className="flex justify-between py-1"><span>Subtotal flete</span><span className="tabular">{formatMXN(subtotalFlete)}</span></div>
                <div className="flex justify-between py-1 border-t border-border mt-1 pt-1.5"><span>Subtotal</span><span className="tabular font-medium">{formatMXN(subtotalCosto)}</span></div>
                <div className="flex justify-between py-1"><span>Indirecto {form.indirecto_pct}%</span><span className="tabular">{formatMXN(indirecto)}</span></div>
                <div className="flex justify-between py-1"><span>Utilidad {form.utilidad_pct}%</span><span className="tabular">{formatMXN(utilidad)}</span></div>
                <div className="flex justify-between py-1"><span>Comisión x venta</span><span className="tabular">{formatMXN(comision)}</span></div>
                <div className="flex justify-between py-2 bg-[#2C2A28] text-white -mx-3 px-3 mt-2"><span className="font-medium uppercase tracking-wide">P.U. (Subtotal sin IVA)</span><span className="tabular font-display text-lg" data-testid="tot-subtotal-sin-iva">{formatMXN(subtotalSinIVA)}</span></div>
                <p className="text-[10px] text-muted-foreground mt-2">Este monto se aplica automáticamente como Precio Unitario de la partida. El IVA se calcula a nivel de cotización.</p>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="form-cancel">Cancelar</Button>
            <Button onClick={save} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="form-save">{editing ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
